import { useCurrency } from '../../../hooks/useCurrency';
import { getDisplayPrice } from '../../../lib/currency';
import { STUDENT_DISCOUNT_DISPLAY, STUDENT_DISCOUNT_REAL } from '../../../mocks/studentCodes';

interface SelectedProduct {
  id: string;
  name: string;
  price: number;
  oldPrice?: number;
  image: string;
  type: string;
}

interface OrderSummaryProps {
  office: SelectedProduct | null;
  antivirus: SelectedProduct | null;
  vpn: SelectedProduct | null;
  promoCode: string;
  promoApplied: boolean;
  promoError: string;
  onPromoChange: (code: string) => void;
  onApplyPromo: () => void;
  onRemovePromo: () => void;
  onAddToCart: () => void;
}

export default function OrderSummary({
  office,
  antivirus,
  vpn,
  promoCode,
  promoApplied,
  promoError,
  onPromoChange,
  onApplyPromo,
  onRemovePromo,
  onAddToCart,
}: OrderSummaryProps) {
  const { currency } = useCurrency();
  const selected = [office, antivirus, vpn].filter(Boolean) as SelectedProduct[];
  const subtotal = selected.reduce((sum, p) => sum + p.price, 0);

  // Descuento real del 5%
  const discountAmount = promoApplied ? Math.round(subtotal * (STUDENT_DISCOUNT_REAL / 100)) : 0;
  const total = subtotal - discountAmount;

  // Para mostrar al usuario como si fuera 10%
  const displayDiscountPercent = STUDENT_DISCOUNT_DISPLAY;

  const allSelected = office && antivirus && vpn;

  const getPrice = (price: number) => {
    return getDisplayPrice(price, undefined, currency).main;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 sticky top-28">
      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <i className="ri-file-list-3-line text-teal-600"></i>
        Resumen del Paquete
      </h3>

      {selected.length === 0 ? (
        <div className="text-center py-8">
          <i className="ri-shopping-basket-line text-4xl text-gray-300 mb-2"></i>
          <p className="text-sm text-gray-400">Selecciona tus productos</p>
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          {selected.map((product) => (
            <div key={product.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2.5">
              <div className="w-10 h-10 flex-shrink-0 bg-white rounded-md flex items-center justify-center p-1">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 line-clamp-1">{product.name}</p>
                <p className="text-[10px] text-gray-500">{product.type}</p>
              </div>
              <span className="text-xs font-bold text-gray-900 whitespace-nowrap">{getPrice(product.price)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Código promocional */}
      {allSelected && (
        <div className="mb-4">
          <label className="text-xs font-semibold text-gray-700 mb-1.5 block">
            <i className="ri-coupon-3-line mr-1 text-teal-600"></i>
            Código de estudiante
          </label>
          {promoApplied ? (
            <div className="flex items-center justify-between bg-teal-50 border border-teal-200 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                <i className="ri-check-double-line text-teal-600"></i>
                <span className="text-sm font-bold text-teal-700">{promoCode}</span>
                <span className="text-xs text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full font-medium">
                  -{displayDiscountPercent}% aplicado
                </span>
              </div>
              <button
                onClick={onRemovePromo}
                className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          ) : (
            <div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => onPromoChange(e.target.value.toUpperCase())}
                  placeholder="Ej: EST-2025-XXXXX"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <button
                  onClick={onApplyPromo}
                  disabled={!promoCode.trim()}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Aplicar
                </button>
              </div>
              {promoError && (
                <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {promoError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Totales */}
      {selected.length > 0 && (
        <div className="border-t border-gray-100 pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal ({selected.length} productos)</span>
            <span className="font-medium text-gray-900">{getPrice(subtotal)}</span>
          </div>
          {promoApplied && (
            <div className="flex justify-between text-sm">
              <span className="text-teal-600 font-medium">
                <i className="ri-gift-line mr-1"></i>
                Descuento estudiante (-{displayDiscountPercent}%)
              </span>
              <span className="font-bold text-teal-600">-{getPrice(discountAmount)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold border-t border-gray-100 pt-3 mt-2">
            <span className="text-gray-900">Total</span>
            <span className="text-gray-900">{getPrice(total)}</span>
          </div>
          {promoApplied && (
            <p className="text-[10px] text-teal-600 text-right font-medium">
              <i className="ri-sparkling-line mr-0.5"></i>
              Ahorras {getPrice(discountAmount)} con tu código de estudiante
            </p>
          )}
        </div>
      )}

      <button
        onClick={onAddToCart}
        disabled={!allSelected}
        className={`w-full mt-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-2 ${
          allSelected
            ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700 shadow-lg shadow-teal-200 hover:shadow-xl cursor-pointer active:scale-[0.98]'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
      >
        <i className="ri-shopping-cart-2-line"></i>
        {allSelected ? 'Agregar paquete al carrito' : `Selecciona ${3 - selected.length} producto${3 - selected.length !== 1 ? 's' : ''} más`}
      </button>

      {!allSelected && (
        <p className="text-[10px] text-gray-400 text-center mt-2">
          Debes seleccionar un producto de cada categoría
        </p>
      )}

      {/* BANNER DE CONSEJO - SIEMPRE VISIBLE */}
      <div className="mt-4 bg-teal-50/60 border border-teal-200 rounded-xl p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex-shrink-0 bg-teal-500 rounded-xl flex items-center justify-center">
            <i className="ri-lightbulb-flash-fill text-white text-lg"></i>
          </div>
          <div>
            <p className="font-bold text-sm text-teal-800 mb-1">💡 Consejo de ahorro</p>
            <p className="text-xs text-teal-700 leading-relaxed">
              ¡No te limites a uno! 🚀 Usa tu código estudiantil y llévate 2 o más productos para disfrutar de su totalidad 10% de descuento. Cuanto más necesites, ¡más ahorras! 🎓
            </p>
          </div>
        </div>
      </div>

      {/* SECCIÓN DE CONTACTO - ESTUDIANTES EN RUSIA */}
      <div className="mt-4 bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-4 shadow-lg">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 flex-shrink-0 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <span className="text-xl">🇷🇺</span>
          </div>
          <div>
            <h4 className="font-bold text-sm text-white mb-1 flex items-center gap-2">
              ¿Vas a estudiar en Rusia?
              <span className="bg-teal-500/30 text-teal-200 text-[9px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                ASESORÍA GRATIS
              </span>
            </h4>
            <p className="text-xs text-white/80 leading-relaxed">
              ¿Preparado para tu aventura académica? Te contamos todo lo que necesitas saber: desde cómo mantenerte conectado 📶 hasta las mejores herramientas digitales y TIPS que harán tu vida universitaria y día a día mucho más fácil. 🎓✨
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <a
            href="https://wa.me/79534940353?text=Hola%20podrias%20asesorarme%20que%20necesito%20mas%20para%20estudiar%20en%20Rusia"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-2.5 transition-all cursor-pointer group"
          >
            <div className="w-8 h-8 flex-shrink-0 bg-green-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <i className="ri-whatsapp-line text-white text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white">WhatsApp</p>
              <p className="text-[10px] text-white/70">Chatea con nosotros ahora</p>
            </div>
            <i className="ri-arrow-right-line text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all"></i>
          </a>

          <a
            href="https://t.me/Sup0rteWin?text=Hola%20podrias%20asesorarme%20que%20necesito%20mas%20para%20estudiar%20en%20Rusia"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-2.5 transition-all cursor-pointer group"
          >
            <div className="w-8 h-8 flex-shrink-0 bg-blue-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <i className="ri-telegram-line text-white text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white">Telegram</p>
              <p className="text-[10px] text-white/70">Escríbenos por Telegram</p>
            </div>
            <i className="ri-arrow-right-line text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all"></i>
          </a>
        </div>

        <div className="mt-3 pt-3 border-t border-white/10">
          <p className="text-[10px] text-white/60 text-center leading-relaxed">
            <i className="ri-time-line mr-1"></i>
            Respondemos en minutos • Asesoría personalizada sin costo
          </p>
        </div>
      </div>
    </div>
  );
}
